import { isNil, omit, omitBy } from "lodash";
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useQuery from "Hooks/query.hook";
import useSession from "Hooks/session.hook";
import usePagination from "Hooks/pagination.hook";
import {
  listLines,
  listServicePoints,
  fetchServicePointTypes,
  updateServicePoint,
  updateServicePointType,
} from "Services/delivery-settings.services";
import { toaster } from "Helpers/toaster";
import { Tabs } from "@life-line-lab/components-react";
import {
  ServicePointList,
  ServicePointTypeList,
} from "Components/delivery-management/delivery-lists";

const ServicePoints: React.FC<any> = () => {
  const { t } = useTranslation();
  const { org } = useSession();
  const history = useHistory();
  const query = useQuery();

  const tabKey = query.get("tabKey") || "0";

  const initialFilters: any = {};

  const [settings, setSettings] = useState<any>({});
  const [response, setResponse] = useState<any>([]);
  const [activeTabKey, setActiveTabKey] = useState(tabKey);
  const [filters, setFilters] = useState<any>(initialFilters);
  const [currentListCount, setCurrentListCount] = useState(0);
  const { paginationComponent, itemPerPage, queryParams, setQueryParams } =
    usePagination({
      currentListCount,
      resetPageDeps: filters
    });

  useEffect(() => {
    tabKey === "0"
      ? history.push(
          `/delivery-management/settings/service-points?page=${queryParams.page}`,
        )
      : history.push(
          `/delivery-management/settings/service-points?tabKey=${tabKey}&page=${queryParams.page}`,
        );
  }, [queryParams.page]);

  useEffect(() => {
    switch (tabKey) {
      case "0":
        handleFetchPointTypes(filters);
        break;
      case "1":
        handleFetchServicePoints(filters);
        break;
      default:
        break;
    }
    return () => {
      setResponse([]);
    };
  }, [tabKey, filters, queryParams.offset, itemPerPage]);

  const handleFilters = (filterInput) => {
    setFilters({
      type: filterInput.byType || null,
      line: filterInput.byLine || null,
      orFields: filterInput.orFields || null,
    });
  };

  const handleFetchPointTypes = async (filters?: any) => {
    const responseData = await fetchServicePointTypes(
      omitBy(filters, isNil),
      queryParams.offset,
      itemPerPage,
    );
    if (responseData && !responseData.error) {
      setResponse(responseData);
      setCurrentListCount(responseData.length);
    }
  };

  const handleFetchServicePoints = async (filters?: any) => {
    const responseData = await listServicePoints(
      omitBy(filters, isNil),
      queryParams.offset,
      itemPerPage,
    );
    if (responseData && !responseData.error) {
      setResponse(responseData);
      setCurrentListCount(responseData.length);
    }
  };

  const handleFetchTypes = async () => {
    const responseData = await fetchServicePointTypes();
    if (responseData && !responseData.error) {
      setSettings((settings) => {
        return { ...settings, TYPE: responseData };
      });
    }
  };

  const handleFetchLines = async () => {
    const responseData = await listLines();
    if (responseData && !responseData.error) {
      setSettings((settings) => {
        return { ...settings, LINE: responseData };
      });
    }
  };

  const handleUpdateServicePointStatus = async (value: string, data: any) => {
    data.orgID = org._id;
    data.active = value == "true";
    data.lineIDs = data.lines.map((line) => line._id);
    data.pointTypeID = data.pointType.map((type) => type._id);
    data.stationCodes = data.stations
    let responseData = await updateServicePoint(
      omit(data, ["_rev", "pointType", "lines", "stations"]),
    );
    if (!isNil(responseData) && responseData.success) {
      handleFetchServicePoints();
      toaster("success", t("toaster.updateSuccessful"));
    } else {
      toaster("error", t("toaster.updateFailed"));
    }
  };

  const handleUpdatePointTypeStatus = async (value: string, data: any) => {
    data.orgID = org._id;
    data.active = value == "true";
    let responseData = await updateServicePointType(omit(data, ["_rev"]));
    if (!isNil(responseData) && responseData.success) {
      handleFetchPointTypes();
      toaster("success", t("toaster.updateSuccessful"));
    } else {
      toaster("error", t("toaster.updateFailed"));
    }
  };

  const tabs = [
    {
      tabKey: "0",
      label: t("label.servicePointsTypes"),
      component: (
        <ServicePointTypeList
          offset={queryParams.offset}
          list={response}
          onFilter={handleFilters}
          onUpdateStatus={handleUpdatePointTypeStatus}
        />
      ),
    },
    {
      tabKey: "1",
      label: t("label.servicePoints"),
      component: (
        <ServicePointList
          offset={queryParams.offset}
          list={response}
          settings={settings}
          onFilter={handleFilters}
          onFetchLines={handleFetchLines}
          onFetchTypes={handleFetchTypes}
          onUpdateStatus={handleUpdateServicePointStatus}
        />
      ),
    },
  ];

  const handleTabClick = (key: string) => {
    setActiveTabKey(key);
    setQueryParams({ page: 1, offset: 0 });
    key === "0"
      ? history.push(`/delivery-management/settings/service-points?page=1`)
      : history.push(
          `/delivery-management/settings/service-points?tabKey=${key}&page=1`,
        );
  };

  return (
    <div className="delivery-management service-points">
      <Helmet>
        <title>{t("pageTitle.servicePoints")}</title>
      </Helmet>

      <div className="title mb-5">{t("title.servicePoints")}</div>

      <Tabs
        destroyInactiveTabPane={true}
        onTabClick={handleTabClick}
        defaultActiveKey="0"
        activeKey={activeTabKey}
        tabBarGutter={25}
        tabs={tabs}
      />
      <div className="my-30">{paginationComponent}</div>
    </div>
  );
};

export default ServicePoints;
